/**
 * Export signature pads to trimmed PNG blobs.
 */

export async function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  const trimmed = trimTransparent(canvas);
  if (trimmed.width <= 1 && trimmed.height <= 1) {
    throw new Error('Signature is empty — draw or type something first');
  }
  return new Promise((resolve, reject) => {
    trimmed.toBlob(
      (blob) => {
        if (!blob) reject(new Error('Failed to export signature'));
        else resolve(blob);
      },
      'image/png'
    );
  });
}

/** Crop nearly-transparent margins; keep a small padding. */
export function trimTransparent(
  source: HTMLCanvasElement,
  alphaThreshold = 8,
  padding = 8
): HTMLCanvasElement {
  const w = source.width;
  const h = source.height;
  if (!w || !h) return source;

  const ctx = source.getContext('2d');
  if (!ctx) return source;
  const { data } = ctx.getImageData(0, 0, w, h);

  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const a = data[(y * w + x) * 4 + 3];
      if (a > alphaThreshold) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX < 0) {
    // Empty — return a tiny transparent canvas
    const empty = document.createElement('canvas');
    empty.width = 1;
    empty.height = 1;
    return empty;
  }

  minX = Math.max(0, minX - padding);
  minY = Math.max(0, minY - padding);
  maxX = Math.min(w - 1, maxX + padding);
  maxY = Math.min(h - 1, maxY + padding);

  const tw = maxX - minX + 1;
  const th = maxY - minY + 1;
  const out = document.createElement('canvas');
  out.width = tw;
  out.height = th;
  const outCtx = out.getContext('2d');
  if (!outCtx) return source;
  outCtx.drawImage(source, minX, minY, tw, th, 0, 0, tw, th);
  return out;
}

export function isCanvasBlank(canvas: HTMLCanvasElement, alphaThreshold = 8): boolean {
  const ctx = canvas.getContext('2d');
  if (!ctx) return true;
  const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] > alphaThreshold) return false;
  }
  return true;
}

/** Render typed text onto a transparent canvas using a script font. */
export function renderTextSignature(options: {
  text: string;
  fontFamily: string;
  color: string;
  fontSize?: number;
}): HTMLCanvasElement {
  const text = options.text.trim();
  const fontSize = options.fontSize ?? 72;
  const font = `${fontSize}px ${options.fontFamily}`;

  const measure = document.createElement('canvas');
  const mctx = measure.getContext('2d');
  if (!mctx) {
    const empty = document.createElement('canvas');
    empty.width = 1;
    empty.height = 1;
    return empty;
  }
  mctx.font = font;
  const metrics = mctx.measureText(text || ' ');
  const width = Math.ceil(Math.max(metrics.width, 40) + 48);
  const height = Math.ceil(fontSize * 1.8);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  ctx.clearRect(0, 0, width, height);
  ctx.font = font;
  ctx.fillStyle = options.color;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 24, height / 2);
  return canvas;
}

export async function fileToFittedCanvas(
  file: File,
  maxWidth = 640,
  maxHeight = 240
): Promise<HTMLCanvasElement> {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}
